import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { alertManagementService } from '../../../services/alertManagementService';

const EscalationConfigModal = ({ userId, onClose }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    severity_level: 'critical',
    escalation_channel: 'email',
    escalation_delay_minutes: 15,
    recipient_email: '',
    recipient_phone: ''
  });

  useEffect(() => {
    loadRules();
  }, [userId]);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await alertManagementService?.getEscalationRules(userId);
      setRules(data);
    } catch (error) {
      console.error('Error loading escalation rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = async () => {
    try {
      await alertManagementService?.createEscalationRule({
        ...formData,
        user_id: userId
      });
      await loadRules();
      setShowAddForm(false);
      setFormData({
        severity_level: 'critical',
        escalation_channel: 'email',
        escalation_delay_minutes: 15,
        recipient_email: '',
        recipient_phone: ''
      });
    } catch (error) {
      console.error('Error creating escalation rule:', error);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm('Are you sure you want to delete this escalation rule?')) return;
    try {
      await alertManagementService?.deleteEscalationRule(ruleId);
      await loadRules();
    } catch (error) {
      console.error('Error deleting escalation rule:', error);
    }
  };

  const handleToggleActive = async (ruleId, isActive) => {
    try {
      await alertManagementService?.updateEscalationRule(ruleId, { is_active: !isActive });
      await loadRules();
    } catch (error) {
      console.error('Error updating escalation rule:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Escalation Rules</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Add Rule Button */}
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mb-6"
            >
              <Plus className="w-4 h-4" />
              Add Escalation Rule
            </button>
          )}

          {/* Add Form */}
          {showAddForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">New Escalation Rule</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Severity Level
                  </label>
                  <select
                    value={formData?.severity_level}
                    onChange={(e) => setFormData({ ...formData, severity_level: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Escalation Channel
                  </label>
                  <select
                    value={formData?.escalation_channel}
                    onChange={(e) => setFormData({ ...formData, escalation_channel: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="both">Both</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delay (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData?.escalation_delay_minutes}
                    onChange={(e) => setFormData({ ...formData, escalation_delay_minutes: parseInt(e?.target?.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recipient Email
                  </label>
                  <input
                    type="email"
                    value={formData?.recipient_email}
                    onChange={(e) => setFormData({ ...formData, recipient_email: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {(formData?.escalation_channel === 'sms' || formData?.escalation_channel === 'both') && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recipient Phone
                    </label>
                    <input
                      type="tel"
                      value={formData?.recipient_phone}
                      onChange={(e) => setFormData({ ...formData, recipient_phone: e?.target?.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={handleAddRule}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save Rule
                </button>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Rules List */}
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : rules?.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No escalation rules configured
            </div>
          ) : (
            <div className="space-y-4">
              {rules?.map((rule) => (
                <div key={rule?.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium capitalize">
                          {rule?.severity_level}
                        </span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium capitalize">
                          {rule?.escalation_channel}
                        </span>
                        <span className="text-sm text-gray-600">
                          Delay: {rule?.escalation_delay_minutes} min
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        {rule?.recipient_email && (
                          <p>Email: {rule?.recipient_email}</p>
                        )}
                        {rule?.recipient_phone && (
                          <p>Phone: {rule?.recipient_phone}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(rule?.id, rule?.is_active)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          rule?.is_active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {rule?.is_active ? 'Active' : 'Inactive'}
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule?.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EscalationConfigModal;